import React from "react";
import type { Lang } from "@/content/translations";

export type CoverSize = "sm" | "md" | "lg";

// Natural aspect ratios of the cropped cover JPEGs in public/covers/.
// Re-run scripts/process-covers.js and update these numbers if the source art
// is ever replaced — BookCover sizes its container from these so there is
// never any letterbox / pillarbox inside the frame.
const NATURAL_AR: Record<Lang, number> = {
  ar: 0.676, // 461 / 682 — front half with 5% back-cover-bleed gutter removed
  en: 0.627, // 538 / 858 — symmetric 40px top/bottom pad (well under prev 105px)
};

// One width per size; height is computed from the per-language aspect below.
const WIDTH: Record<CoverSize, number> = {
  sm: 220,
  md: 340,
  lg: 440,
};

interface BookCoverProps {
  lang: Lang;
  size?: CoverSize;
  className?: string;
  alt?: string;
  /**
   * If true, renders both front and back faces in a 3D-flippable
   * container with a small toggle button overlay. The parent
   * controls the flip state (so the button label can be localised
   * via the page-level translations) — pass `flipped` and `onFlip`.
   */
  flippable?: boolean;
  flipped?: boolean;
  onFlip?: () => void;
  /**
   * The URL of the back-cover image. Required when `flippable` is true
   * AND no `backOverlay` is provided. Ignored otherwise. Should share
   * the same natural aspect as the front cover for the flip to read
   * as a single book spinning in place.
   */
  backImageSrc?: string;
  /**
   * Alternative back-face content (a JSX node) for languages whose
   * physical book has no back-cover artwork. Renders inside a panel
   * styled to match the front cover's frame. If provided, `backImageSrc`
   * is ignored.
   */
  backOverlay?: React.ReactNode;
  /** Localised label for the flip button. Defaults to "Flip". */
  flipLabel?: string;
  /** Localised back-of-cover image alt text. */
  backAlt?: string;
}

/**
 * Renders the real book cover photograph for Arabic, and the English
 * poster artwork for English. When `flippable` is true, the container
 * becomes a 3D-flippable card showing the back cover (or a custom
 * `backOverlay`) on the reverse side.
 */
export const BookCover: React.FC<BookCoverProps> = ({
  lang,
  size = "md",
  className = "",
  alt,
  flippable = false,
  flipped = false,
  onFlip,
  backImageSrc,
  backOverlay,
  flipLabel,
  backAlt,
}) => {
  const w = WIDTH[size];
  const h = Math.round(w / NATURAL_AR[lang]);
  const base = import.meta.env.BASE_URL;
  const src =
    lang === "ar" ? `${base}covers/ar-cover.jpg` : `${base}covers/en-poster.jpg`;
  const fallbackAlt =
    lang === "ar"
      ? "غلاف كتاب الحضارة المصرية القديمة — رؤية عقلية موثّقة"
      : "English edition book poster — title to be revealed";

  // Responsive container: width caps at the design `w` on desktop and
  // shrinks to 100% of its parent on narrow viewports. CSS aspect-ratio
  // keeps the proportions whatever the resolved width turns out to be.
  const containerStyle: React.CSSProperties = {
    width: w,
    maxWidth: "100%",
    aspectRatio: NATURAL_AR[lang],
  };

  // Non-flippable path keeps the original simple markup so existing
  // pages that don't pass `flippable` are unaffected.
  if (!flippable) {
    return (
      <div
        className={`relative overflow-hidden ${className}`}
        style={{ ...containerStyle, background: "#0b0908" }}
      >
        <img
          src={src}
          alt={alt ?? fallbackAlt}
          width={w}
          height={h}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    );
  }

  // 3D flip path. Outer container provides perspective, inner card is
  // the rotating element with both faces stacked. Each face uses
  // backface-visibility:hidden so only the visible side draws — the
  // hidden side doesn't bleed through translucent edges.
  const backSrc = backImageSrc;
  const flipBtnLabel = flipLabel ?? "Flip cover";

  return (
    <div
      className={`relative ${className}`}
      style={{ ...containerStyle, perspective: 1200 }}
    >
      <div
        className="relative h-full w-full"
        style={{
          transformStyle: "preserve-3d",
          transition: "transform 700ms cubic-bezier(.2,.7,.2,1)",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front face */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            background: "#0b0908",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <img
            src={src}
            alt={alt ?? fallbackAlt}
            width={w}
            height={h}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        {/* Back face — either a real back-cover image OR a custom panel */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            background: "#0b0908",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {backOverlay ? (
            backOverlay
          ) : backSrc ? (
            <img
              src={backSrc}
              alt={backAlt ?? (alt ?? fallbackAlt)}
              width={w}
              height={h}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
        </div>
      </div>

      {/* Flip toggle. Sits below the cover so it never occludes the
          artwork or the bio text on the back. The parent (Book/English
          page) supplies the localised label and the click handler.
          Colours come from the nav-bg / nav-text theme tokens, which
          flip automatically with light/dark mode — the button reads
          dark-on-parchment in light theme and parchment-on-dark in
          dark theme without any per-theme code paths. */}
      {onFlip && (
        <button
          type="button"
          onClick={onFlip}
          className="absolute -bottom-12 inset-x-0 mx-auto block px-4 py-2 text-[11px] tracked-wide ff-en-sc rounded-full"
          style={{
            background: "var(--nav-bg)",
            color: "var(--nav-text)",
            border: "1px solid var(--hairline-dark)",
            width: "fit-content",
            backdropFilter: "blur(4px)",
          }}
          aria-pressed={flipped}
        >
          {flipBtnLabel}
        </button>
      )}
    </div>
  );
};
